<script lang="ts">
    import {getCurrentLanguage, getInjector, getLocalization} from 'src/core';
    import AnimatedCounter from './AnimatedCounter.svelte';
    import {LandingPageStatsDataService} from './landing-page-stats.data-service';
    import {fade} from 'svelte/transition';
    import TransitionWrapper from './TransitionWrapper.svelte';
    import KpPluralizeText from '../kp-pluralize-text/KpPluralizeText.svelte';
    import {labelLocalizationsMap, noteLocalizationsMap} from './localizations-maps';
    import VerbisPictogramOrange from '../../brand/VerbisPictogramOrange.svelte';

    const localize = getLocalization();
    const currentLanguage = getCurrentLanguage();
    const landingPageStatsDataService = getInjector().getByClass(LandingPageStatsDataService);

    const data = landingPageStatsDataService.get().then((search) => search.result);

    const stats = {
        documentsCount: data.then((result) => result.documentsCount),
        newDocumentsCount: data.then((result) => result.newDocumentsCount),
        registeredReadersCount: data.then((result) => result.registeredReadersCount),
        activeLoansCount: data.then((result) => result.activeLoansCount),
    }
</script>

<div class="landing-page-stats">
    {#each Object.entries(stats) as [name, valuePromise]}
        {@const [labelForOne, labelForFew, labelForMore] = labelLocalizationsMap[name]}
        {@const [noteForOne, noteForFew, noteForMore] = noteLocalizationsMap[name]}
        <div class="stat">
            <div class="stat-value">
                <TransitionWrapper>
                    {#await valuePromise}
                        <div class="pulse-loader" out:fade|global={{duration:200}}></div>
                    {:then value}
                        <div in:fade|global={{duration:200}}>
                            <AnimatedCounter value={value.count} locale={currentLanguage}/>
                        </div>
                    {/await}
                </TransitionWrapper>
            </div>
            <VerbisPictogramOrange style="width: 86px;"/>
            <TransitionWrapper>
                {#await valuePromise}
                    <div class="stat-label" out:fade|global={{duration:200}}>
                        <KpPluralizeText count={0}
                                         zeroCountText={localize(labelForMore)}
                                         oneCountText={localize(labelForOne)}
                                         twoThreeFourCountText={localize(labelForFew)}
                                         moreText={localize(labelForMore)}/>
                    </div>
                {:then value}
                    <div class="stat-label" in:fade|global={{duration:200}}>
                        <KpPluralizeText count={value.count}
                                         zeroCountText={localize(labelForMore)}
                                         oneCountText={localize(labelForOne)}
                                         twoThreeFourCountText={localize(labelForFew)}
                                         moreText={localize(labelForMore)}/>
                    </div>
                {/await}
            </TransitionWrapper>
            <TransitionWrapper>
                {#await valuePromise}
                    <div class="stat-note" out:fade|global={{duration:200}}>
                        <KpPluralizeText count={0}
                                         zeroCountText={localize(noteForMore)}
                                         oneCountText={localize(noteForOne)}
                                         twoThreeFourCountText={localize(noteForFew)}
                                         moreText={localize(noteForMore)}/>
                    </div>
                {:then value}
                    <div class="stat-note" in:fade|global={{duration:200}}>
                        <KpPluralizeText count={value.count}
                                         zeroCountText={localize(noteForMore)}
                                         oneCountText={localize(noteForOne)}
                                         twoThreeFourCountText={localize(noteForFew)}
                                         moreText={localize(noteForMore)}/>
                    </div>
                {/await}
            </TransitionWrapper>
        </div>
    {/each}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.media-queries.less";

    :global(.pulse-loader) {
        width: 1em;
        height: 1em;
        border-radius: 1em;
        background-color: @themed-body-bg-orange-highlighted;
        outline: 1px solid transparent;
        animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
        0% {
            transform: scale(0);
            opacity: 1;
        }
        100% {
            transform: scale(1);
            opacity: 0;
        }
    }

    .landing-page-stats {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: 1fr;
        gap: 2em;

        @media (max-width: @screen-sm-max) {
            display: flex;
            flex-direction: column;
            gap: 3.5em;
        }
    }

    .landing-page-stats {
        .stat {
            display: flex;
            flex-direction: column;
            align-items: center;
            line-height: 1;
            gap: @spacing-s;
        }

        .stat-label {
            font-size: @font-size-xxl;
            color: @themed-text-default;
            margin-top: @spacing-m;
        }

        .stat-note {
            font-size: @font-size-default;
            color: @themed-text-muted;
        }

        .stat-value {
            font-size: 4em;
            font-weight: bold;
            color: @themed-text-default;
        }
    }
</style>
